<div class="page-container">
  <app-page-header
    title="Manage Station Rates"
    subtitle="Configure pricing and rate cards for radio stations"
    [showBackButton]="true"
    backRoute="/userhome"
    backLabel="Back to Dashboard">
    <button class="btn-secondary btn-compact" (click)="refreshData()" [disabled]="loading" title="Refresh data">
      <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
      </svg>
      Refresh
    </button>
    <button *ngIf="canAdd" class="btn-secondary btn-compact" (click)="openBulkRatesDialog()" [disabled]="loading || !selectedStation">
      <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
      </svg>
      Add Bulk Rates
    </button>
    <button *ngIf="canAdd" class="btn-primary btn-compact" (click)="openAddDialog()" [disabled]="loading || !selectedStation">
      <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
      </svg>
      Add Rates
    </button>
  </app-page-header>

  <app-station-header
    [stations]="stations"
    [selectedStation]="selectedStation"
    [loading]="loadingStations"
    title="Select Station"
    subtitle="Choose a station to manage rates"
    (stationChange)="onStationChange($event)">
  </app-station-header>

  <div class="content-wrapper" *ngIf="selectedStation">
    <div class="rates-pricing-note" role="note">
      <strong>Note:</strong>
      The calculated pricing value is based on the current Station Ratings and may change when
      Station Ratings are updated. Existing orders are not affected and will continue using the
      pricing values that were applied when they were created.
    </div>

    <div class="table-toolbar" *ngIf="stationRates.length > 0 || isBulkSelectMode">
      <div class="bulk-toolbar" *ngIf="isBulkSelectMode; else selectModeBtn">
        <span class="selected-count">{{ selectedCount }} selected</span>
        <div class="bulk-toolbar-actions">
          <button type="button" class="btn-ghost" (click)="selectAllRates()">Select all</button>
          <button type="button" class="btn-ghost" (click)="clearSelection()">Clear</button>
          <button
            type="button"
            class="btn-danger-soft"
            [disabled]="selectedCount === 0 || isDeletingBulk"
            (click)="confirmBulkDelete()">
            {{ isDeletingBulk ? 'Deleting…' : 'Delete (' + selectedCount + ')' }}
          </button>
          <button type="button" class="btn-ghost" (click)="toggleBulkSelectMode()">Done</button>
        </div>
      </div>
      <ng-template #selectModeBtn>
        <button
          *ngIf="canDelete && stationRates.length > 0"
          type="button"
          class="btn-select-mode"
          (click)="toggleBulkSelectMode()">
          Select multiple
        </button>
      </ng-template>
    </div>

    <app-common-table
      [data]="stationRates"
      [columns]="tableColumns"
      [actions]="tableActions"
      [loading]="loading"
      emptyMessage="No Station Rates Found"
      emptyDescription="There are no rates configured for this station. You can add new rates manually or create multiple rates at once."
      [showAddButton]="canAdd"
      addButtonText="Add New Rates"
      (addClick)="openAddDialog()">

      <ng-template #headerTemplate>
        <th *ngIf="isBulkSelectMode" class="th-check" aria-label="Select"></th>
        <th class="th-name">Package Name</th>
        <th class="th-spot">Spot</th>
        <th class="th-time">Time</th>
        <th class="th-days">Days</th>
        <th class="th-source">Entered As</th>
        <th class="th-cpm">CPM</th>
        <th class="th-rate">Rate Card</th>
        <th class="th-actions">Actions</th>
      </ng-template>

      <ng-template #rowTemplate let-rate>
        <tr class="table-row" [class.selected-row]="selectedRateIds.has(rate.id)">
          <td *ngIf="isBulkSelectMode" class="td-check">
            <label class="row-check">
              <input
                type="checkbox"
                [checked]="selectedRateIds.has(rate.id)"
                (change)="toggleRateSelection(rate.id)"
                (click)="$event.stopPropagation()"
                [attr.aria-label]="'Select rate ' + rate.friendly_name">
              <span class="row-check-box" aria-hidden="true"></span>
            </label>
          </td>
          <td class="td-name">{{ rate.friendly_name }}</td>
          <td class="td-spot">{{ rate.spot_length }}s</td>
          <td class="td-time">{{ formatTimeRange(rate.start_hour, rate.end_hour) }}</td>
          <td class="td-days">{{ formatDays(rate.days) }}</td>
          <td class="td-source">{{ formatPricingType(rate.pricing_type) }}</td>
          <td class="td-cpm">{{ formatCPM(rate.cpm) }}</td>
          <td class="td-rate">{{ formatCurrency(rate.ratecard) }}</td>
          <td class="td-actions">
            <div class="action-buttons">
              <ng-container *ngFor="let action of tableActions">
              <button
                *ngIf="!action.show || action.show(rate)"
                type="button"
                [class]="action.class"
                (click)="action.callback(rate)"
                [title]="action.label"
                [attr.aria-label]="action.label">
                <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path *ngIf="action.icon === 'edit'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  <path *ngIf="action.icon === 'delete'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
              </ng-container>
            </div>
          </td>
        </tr>
      </ng-template>
    </app-common-table>

    <mat-paginator
      [length]="total"
      [pageSize]="limit"
      [pageSizeOptions]="pageSizeOptions"
      [pageIndex]="offset / limit"
      (page)="onPageChange($event)"
      aria-label="Select page">
    </mat-paginator>
  </div>

  <div class="no-station-card" *ngIf="!selectedStation && !loadingStations">
    <div class="no-station-content">
      <svg class="no-station-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
      </svg>
      <h3>Select a Station</h3>
      <p>Choose a station from the dropdown above to view and manage its rates.</p>
    </div>
  </div>
</div>
